/**
 * routes/dashboard.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Traveler Accounts V1 — personal dashboard.
 *
 * Shows:
 *   - Traveler preferences (interests, pace, budget, traveler type)
 *   - Stats bar (journey count, cities explored count)
 *   - Cities explored (pills grid)
 *   - Recent journeys (list from Supabase history)
 *
 * Anonymous compatibility
 * ───────────────────────
 * When the user is not logged in, the page renders with local profileStore
 * data and invites them to log in.  All Supabase calls are skipped.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/auth/AuthContext";
import { isSupabaseEnabled } from "@/lib/supabase";
import type { DbTravelerProfile, DbJourneyHistory } from "@/lib/supabase";
import type { TravelerProfile } from "@/lib/profile/profileStore";

// ─── Route definition ─────────────────────────────────────────────────────────

export const Route = createFileRoute("/dashboard")({
  component: DashboardPage,
  head: () => ({
    meta: [
      { title: "Mi perfil — Sift Trip" },
      { name: "description", content: "Tu historial de viajes y preferencias." },
    ],
  }),
});

// ─── Static data ──────────────────────────────────────────────────────────────

/** IATA → display name + flag emoji for known RHEA cities. */
const CITY_META: Record<string, { name: string; flag: string }> = {
  TYO: { name: "Tokio",           flag: "🇯🇵" },
  KYO: { name: "Kioto",           flag: "🇯🇵" },
  OSA: { name: "Osaka",           flag: "🇯🇵" },
  HKN: { name: "Hakone",          flag: "🇯🇵" },
  KNZ: { name: "Kanazawa",        flag: "🇯🇵" },
  BCN: { name: "Barcelona",       flag: "🇪🇸" },
  MAD: { name: "Madrid",          flag: "🇪🇸" },
  SEV: { name: "Sevilla",         flag: "🇪🇸" },
  GRA: { name: "Granada",         flag: "🇪🇸" },
  COR: { name: "Córdoba",         flag: "🇪🇸" },
  CDZ: { name: "Cádiz",           flag: "🇪🇸" },
  AGP: { name: "Málaga",          flag: "🇪🇸" },
  LIS: { name: "Lisboa",          flag: "🇵🇹" },
  OPO: { name: "Oporto",          flag: "🇵🇹" },
  CBM: { name: "Coimbra",         flag: "🇵🇹" },
  FAO: { name: "Faro",            flag: "🇵🇹" },
  DOV: { name: "Valle del Duero", flag: "🇵🇹" },
  ROM: { name: "Roma",            flag: "🇮🇹" },
  FLR: { name: "Florencia",       flag: "🇮🇹" },
  VCE: { name: "Venecia",         flag: "🇮🇹" },
  NAP: { name: "Nápoles",         flag: "🇮🇹" },
  MXP: { name: "Milán",           flag: "🇮🇹" },
  AMS: { name: "Ámsterdam",       flag: "🇳🇱" },
  BGE: { name: "Brujas",          flag: "🇧🇪" },
  CDG: { name: "París",           flag: "🇫🇷" },
  MEX: { name: "Ciudad de México",flag: "🇲🇽" },
  TOL: { name: "Toledo",          flag: "🇪🇸" },
};

const INTEREST_LABELS: Record<string, string> = {
  food:         "Gastronomía",
  culture:      "Cultura",
  art:          "Arte",
  music:        "Música",
  nightlife:    "Vida nocturna",
  nature:       "Naturaleza",
  architecture: "Arquitectura",
  wellness:     "Bienestar",
  shopping:     "Compras",
  anime:        "Anime / Pop",
};

const PACE_LABELS: Record<string, string> = {
  suave:       "Tranquilo",
  equilibrado: "Equilibrado",
  intenso:     "Intenso",
};

const BUDGET_LABELS: Record<string, string> = {
  economico:   "Económico",
  equilibrado: "Equilibrado",
  premium:     "Premium",
  lujo:        "Lujo",
};

const TRAVELER_LABELS: Record<string, string> = {
  solo:    "Solo",
  couple:  "En pareja",
  pareja:  "En pareja",
  friends: "Con amigos",
  amigos:  "Con amigos",
  family:  "En familia",
  familia: "En familia",
};

const TONE_LABELS: Record<string, { emoji: string; label: string }> = {
  romantic:    { emoji: "💞", label: "Romántico" },
  foodie:      { emoji: "🍷", label: "Gastronómico" },
  hidden_gems: { emoji: "🔍", label: "Descubrimiento" },
  cultural:    { emoji: "🏛",  label: "Cultural" },
  adventure:   { emoji: "⛰",  label: "Aventura" },
  wellness:    { emoji: "🌿", label: "Bienestar" },
  classic:     { emoji: "✦",  label: "Clásico" },
};

// ─── Animation helpers ────────────────────────────────────────────────────────

const fadeUp = (delay = 0) => ({
  initial:    { opacity: 0, y: 20 },
  animate:    { opacity: 1, y: 0  },
  transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] as const },
});

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2
      className="text-[11px] uppercase tracking-[0.22em] font-bold mb-4"
      style={{ color: "oklch(0.545 0.020 60 / 0.50)" }}
    >
      {children}
    </h2>
  );
}

function StatPill({
  value,
  label,
}: {
  value: string | number;
  label: string;
}) {
  return (
    <div
      className="flex flex-col items-center justify-center rounded-2xl px-6 py-4 gap-0.5"
      style={{ background: "oklch(0.972 0.010 75 / 0.70)", border: "1px solid oklch(0.880 0.024 72 / 0.40)" }}
    >
      <span
        className="font-serif font-black leading-none"
        style={{ fontSize: "clamp(1.6rem, 5vw, 2.2rem)", color: "oklch(0.298 0.058 167)" }}
      >
        {value}
      </span>
      <span className="text-[11px] font-medium" style={{ color: "oklch(0.545 0.020 60 / 0.60)" }}>
        {label}
      </span>
    </div>
  );
}

function InterestPill({ label, count }: { label: string; count: number }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium"
      style={{
        background: "oklch(0.972 0.010 75 / 0.80)",
        border:     "1px solid oklch(0.880 0.024 72 / 0.50)",
        color:      "oklch(0.28 0.040 60)",
      }}
    >
      {label}
      <span
        className="text-[10px] font-bold rounded-full px-1.5"
        style={{ background: "oklch(0.298 0.058 167 / 0.12)", color: "oklch(0.298 0.058 167)" }}
      >
        {count}
      </span>
    </span>
  );
}

function CityPill({ iata }: { iata: string }) {
  const meta = CITY_META[iata];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium"
      style={{
        background: "oklch(0.978 0.010 72)",
        border:     "1px solid oklch(0.880 0.024 72 / 0.45)",
        color:      "oklch(0.30 0.025 58)",
      }}
    >
      {meta ? (
        <>
          <span>{meta.flag}</span>
          {meta.name}
        </>
      ) : (
        iata
      )}
    </span>
  );
}

function JourneyCard({ journey }: { journey: DbJourneyHistory }) {
  const tone = journey.journey_tone ? TONE_LABELS[journey.journey_tone] : null;
  const date = new Date(journey.created_at).toLocaleDateString("es-ES", {
    day:   "numeric",
    month: "short",
    year:  "numeric",
  });

  return (
    <div
      className="rounded-2xl px-5 py-4 flex flex-col gap-2"
      style={{
        background: "oklch(0.978 0.010 72)",
        border:     "1px solid oklch(0.880 0.024 72 / 0.45)",
      }}
    >
      {/* Title + tone chip */}
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold text-sm leading-snug" style={{ color: "oklch(0.20 0.020 55)" }}>
          {journey.journey_title}
        </p>
        {tone && (
          <span
            className="shrink-0 text-[11px] font-semibold rounded-full px-2 py-0.5"
            style={{
              background: "oklch(0.298 0.058 167 / 0.10)",
              color:      "oklch(0.298 0.058 167)",
            }}
          >
            {tone.emoji} {tone.label}
          </span>
        )}
      </div>

      {/* Cities */}
      {journey.cities.length > 0 && (
        <p className="text-xs" style={{ color: "oklch(0.545 0.020 60 / 0.65)" }}>
          {journey.cities.join(" · ")}
        </p>
      )}

      {/* Footer: days + date */}
      <div className="flex items-center justify-between mt-1">
        <span className="text-xs font-medium" style={{ color: "oklch(0.545 0.020 60 / 0.50)" }}>
          {journey.total_days} días
        </span>
        <span className="text-xs" style={{ color: "oklch(0.545 0.020 60 / 0.40)" }}>
          {date}
        </span>
      </div>
    </div>
  );
}

// ─── Login prompt (for anonymous visitors) ────────────────────────────────────

function LoginPromptCard({ onLogin }: { onLogin: () => void }) {
  return (
    <motion.div
      {...fadeUp(0.1)}
      className="rounded-3xl px-8 py-10 text-center mb-8"
      style={{
        background:  "linear-gradient(135deg, oklch(0.978 0.014 70) 0%, oklch(0.970 0.010 58) 100%)",
        border:      "1px solid oklch(0.880 0.024 72 / 0.50)",
        boxShadow:   "0 4px 32px oklch(0.298 0.058 167 / 0.06)",
      }}
    >
      <div
        className="w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center"
        style={{ background: "oklch(0.298 0.058 167 / 0.10)" }}
      >
        <svg width="26" height="26" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.5 1.5a6 6 0 100 12 6 6 0 000-12zm0 2.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm0 7.5a4.5 4.5 0 01-3.75-2.013C3.76 8.5 5.47 8 7.5 8s3.74.5 3.75 1.487A4.5 4.5 0 017.5 11.5z"
            fill="oklch(0.298 0.058 167)" />
        </svg>
      </div>
      <h2 className="font-serif font-black text-xl mb-2" style={{ color: "oklch(0.20 0.020 55)" }}>
        Guarda tus viajes
      </h2>
      <p className="text-sm mb-6 max-w-xs mx-auto" style={{ color: "oklch(0.45 0.015 58 / 0.75)" }}>
        Inicia sesión para que Sift Trip recuerde tus preferencias y conserve tu historial de rutas.
      </p>
      {isSupabaseEnabled ? (
        <button
          onClick={onLogin}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.98]"
          style={{ background: "oklch(0.298 0.058 167)" }}
        >
          {/* Google G */}
          <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.68 8.18c0-.57-.05-1.12-.14-1.64H8v3.1h4.28a3.65 3.65 0 01-1.58 2.4v2h2.56c1.5-1.38 2.42-3.4 2.42-5.86z" fill="#fff"/>
            <path d="M8 16c2.16 0 3.97-.71 5.3-1.94l-2.56-2a5.02 5.02 0 01-2.74.76 5 5 0 01-4.7-3.46H.66v2.08A8 8 0 008 16z" fill="#e0e0e0"/>
            <path d="M3.3 9.36A5.03 5.03 0 013.04 8c0-.47.08-.93.26-1.36V4.56H.66A8 8 0 000 8c0 1.29.31 2.51.66 3.44l2.64-2.08z" fill="#ccc"/>
            <path d="M8 3.18c1.22 0 2.3.42 3.16 1.24l2.36-2.36C12 .79 10.16 0 8 0A8 8 0 00.66 4.56l2.64 2.08A5 5 0 018 3.18z" fill="#bbb"/>
          </svg>
          Continuar con Google
        </button>
      ) : (
        <p className="text-xs" style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}>
          Autenticación no configurada en esta instancia.
        </p>
      )}
    </motion.div>
  );
}

// ─── Main dashboard ───────────────────────────────────────────────────────────

function DashboardPage() {
  const { user, signInWithGoogle } = useAuth();

  // ── Data state ────────────────────────────────────────────────────────────
  const [remoteProfile, setRemoteProfile] = useState<DbTravelerProfile | null>(null);
  const [localProfile,  setLocalProfile]  = useState<TravelerProfile  | null>(null);
  const [journeys,      setJourneys]      = useState<DbJourneyHistory[]>([]);
  const [dataLoading,   setDataLoading]   = useState(true);

  // ── Load data on mount / user change ──────────────────────────────────────
  useEffect(() => {
    setDataLoading(true);

    const load = async () => {
      // Always load the local profile for anonymous fallback
      const { loadProfile } = await import("@/lib/profile/profileStore");
      setLocalProfile(loadProfile());

      if (user) {
        // Logged-in: load remote profile + journey history in parallel
        const { loadProfileFromSupabase, loadJourneyHistory } = await import(
          "@/lib/auth/profileSync"
        );
        const [remote, history] = await Promise.all([
          loadProfileFromSupabase(user.id),
          loadJourneyHistory(user.id, 10),
        ]);
        setRemoteProfile(remote);
        setJourneys(history);
      }

      setDataLoading(false);
    };

    load().catch((err) => {
      console.error("[Dashboard] Load failed:", err);
      setDataLoading(false);
    });
  }, [user]);

  // ── Resolve display data: prefer remote when available ────────────────────
  // For anonymous users, show local profile so the page is never empty.
  const interestCounts: Record<string, number> =
    remoteProfile?.interest_counts ??
    localProfile?.interest_counts   ??
    {};

  const topInterests = Object.entries(interestCounts)
    .sort(([, a], [, b]) => b - a)
    .filter(([, c]) => c > 0)
    .slice(0, 6);

  const preferredPace    =
    remoteProfile?.pace ??
    dominantLocal(localProfile?.pace_counts ?? {});

  const preferredBudget  =
    remoteProfile?.budget_style ??
    dominantLocal(localProfile?.budget_style_counts ?? {});

  const preferredType    =
    remoteProfile?.traveler_type ??
    dominantLocal(localProfile?.traveler_type_counts ?? {});

  const citiesExplored: string[] =
    remoteProfile?.cities_explored ??
    localProfile?.cities_explored   ??
    [];

  const generationCount =
    remoteProfile?.generation_count ??
    localProfile?.generation_count  ??
    0;

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (dataLoading) {
    return (
      <div className="min-h-screen max-w-2xl mx-auto px-4 py-12">
        <div className="space-y-4 animate-pulse">
          <div className="h-8 w-48 bg-muted rounded-xl" />
          <div className="h-24 bg-muted rounded-2xl" />
          <div className="h-40 bg-muted rounded-2xl" />
          <div className="h-40 bg-muted rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen max-w-2xl mx-auto px-4 py-10 sm:py-14">

      {/* ── Page header ───────────────────────────────────────────────────── */}
      <motion.div {...fadeUp(0)} className="mb-8">
        <p
          className="text-[10px] uppercase tracking-[0.28em] font-bold mb-2"
          style={{ color: "oklch(0.298 0.058 167 / 0.70)" }}
        >
          ✦ Sift Trip
        </p>
        <h1
          className="font-serif font-black leading-none mb-1"
          style={{
            fontSize:      "clamp(1.9rem, 6vw, 2.8rem)",
            color:         "oklch(0.140 0.012 55)",
            letterSpacing: "-0.02em",
          }}
        >
          {user
            ? (user.user_metadata?.full_name as string | undefined)?.split(" ")[0] ?? "Mi perfil"
            : "Mi perfil"}
        </h1>
        {user ? (
          <p className="text-sm" style={{ color: "oklch(0.545 0.020 60 / 0.55)" }}>
            {user.email}
          </p>
        ) : (
          <p className="text-sm" style={{ color: "oklch(0.545 0.020 60 / 0.55)" }}>
            Modo anónimo — tus preferencias están guardadas localmente.
          </p>
        )}
      </motion.div>

      {/* ── Login prompt for anonymous users ──────────────────────────────── */}
      {!user && (
        <LoginPromptCard onLogin={signInWithGoogle} />
      )}

      {/* ── Stats row ─────────────────────────────────────────────────────── */}
      {generationCount > 0 && (
        <motion.div {...fadeUp(0.07)} className="flex gap-3 mb-8">
          <StatPill value={generationCount} label="rutas generadas" />
          <StatPill value={citiesExplored.length} label={citiesExplored.length === 1 ? "ciudad explorada" : "ciudades exploradas"} />
          {user && journeys.length > 0 && (
            <StatPill value={journeys.length} label={journeys.length === 1 ? "ruta guardada" : "rutas guardadas"} />
          )}
        </motion.div>
      )}

      {/* ── Preferences ───────────────────────────────────────────────────── */}
      {(topInterests.length > 0 || preferredPace || preferredBudget || preferredType) && (
        <motion.section {...fadeUp(0.12)} className="mb-8">
          <SectionHeading>Tus preferencias</SectionHeading>

          <div
            className="rounded-2xl px-5 py-5 space-y-5"
            style={{
              background: "oklch(0.978 0.010 72)",
              border:     "1px solid oklch(0.880 0.024 72 / 0.45)",
            }}
          >
            {/* Top interests */}
            {topInterests.length > 0 && (
              <div>
                <p className="text-xs font-semibold mb-2.5" style={{ color: "oklch(0.40 0.015 58 / 0.65)" }}>
                  Intereses
                </p>
                <div className="flex flex-wrap gap-2">
                  {topInterests.map(([key, count]) => (
                    <InterestPill
                      key={key}
                      label={INTEREST_LABELS[key] ?? key}
                      count={count}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Pace / Budget / Traveler type */}
            {(preferredPace || preferredBudget || preferredType) && (
              <div className="flex flex-wrap gap-4">
                {preferredPace && (
                  <div>
                    <p className="text-[11px] font-semibold mb-1" style={{ color: "oklch(0.40 0.015 58 / 0.55)" }}>
                      Ritmo
                    </p>
                    <span className="text-sm font-medium" style={{ color: "oklch(0.28 0.040 60)" }}>
                      {PACE_LABELS[preferredPace] ?? preferredPace}
                    </span>
                  </div>
                )}
                {preferredBudget && (
                  <div>
                    <p className="text-[11px] font-semibold mb-1" style={{ color: "oklch(0.40 0.015 58 / 0.55)" }}>
                      Presupuesto
                    </p>
                    <span className="text-sm font-medium" style={{ color: "oklch(0.28 0.040 60)" }}>
                      {BUDGET_LABELS[preferredBudget] ?? preferredBudget}
                    </span>
                  </div>
                )}
                {preferredType && (
                  <div>
                    <p className="text-[11px] font-semibold mb-1" style={{ color: "oklch(0.40 0.015 58 / 0.55)" }}>
                      Tipo de viajero
                    </p>
                    <span className="text-sm font-medium" style={{ color: "oklch(0.28 0.040 60)" }}>
                      {TRAVELER_LABELS[preferredType] ?? preferredType}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.section>
      )}

      {/* ── Cities explored ───────────────────────────────────────────────── */}
      {citiesExplored.length > 0 && (
        <motion.section {...fadeUp(0.17)} className="mb-8">
          <SectionHeading>Ciudades exploradas</SectionHeading>
          <div className="flex flex-wrap gap-2">
            {citiesExplored.map((iata) => (
              <CityPill key={iata} iata={iata} />
            ))}
          </div>
        </motion.section>
      )}

      {/* ── Recent journeys (logged-in only) ──────────────────────────────── */}
      {user && journeys.length > 0 && (
        <motion.section {...fadeUp(0.22)} className="mb-8">
          <SectionHeading>Rutas recientes</SectionHeading>
          <div className="flex flex-col gap-3">
            {journeys.map((j) => (
              <JourneyCard key={j.id} journey={j} />
            ))}
          </div>
        </motion.section>
      )}

      {/* ── Empty state for new users with no data yet ────────────────────── */}
      {!dataLoading && generationCount === 0 && (
        <motion.div {...fadeUp(0.15)} className="text-center py-16">
          <div
            className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center"
            style={{ background: "oklch(0.972 0.010 75)" }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 17.07l1.5 1.42z"
                fill="oklch(0.298 0.058 167 / 0.5)"/>
            </svg>
          </div>
          <h3 className="font-serif font-bold text-lg mb-2" style={{ color: "oklch(0.28 0.020 55)" }}>
            Aún no hay datos de viaje
          </h3>
          <p className="text-sm mb-6" style={{ color: "oklch(0.545 0.020 60 / 0.60)" }}>
            Genera tu primera ruta para que Sift Trip empiece a conocer tus preferencias.
          </p>
          <Link
            to="/experiences"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-all hover:opacity-90"
            style={{ background: "oklch(0.298 0.058 167)" }}
          >
            Explorar experiencias
          </Link>
        </motion.div>
      )}

    </div>
  );
}

// ─── Local helper (mirrors dominant() from profileSync, avoids circular import) ─

function dominantLocal(counts: Record<string, number>): string | null {
  const entries = Object.entries(counts).sort(([, a], [, b]) => b - a);
  return entries.length > 0 && entries[0][1] > 0 ? entries[0][0] : null;
}
